/**
 * LiveSyncTab — iter87.9. Redesign to match the GolfSyncCard look
 * operator prefers:
 *
 *   • Header: "LIVE SYNC — <API>"  + tournament / event subhead
 *   • Cadence note + "Auto-synced · cached feed" pill
 *   • NO "Last sync: never" for API-backed feeds — those are cache
 *     reads, the platform poller owns cadence; showing a per-tournament
 *     last-sync timestamp only makes sense for hands-on feeds (CSV /
 *     GotSport). Golf keeps its own timestamp because the Slash Golf
 *     poller stamps it per tournament (`tournament.golf.last_synced_at`).
 *   • Read-only field map below.
 *   • Reassurance line: once the tournament is marked Complete, no
 *     more upstream calls are made (both pollers filter status).
 */
import { Radio, FileText, ExternalLink } from "lucide-react";
import { getTemplateSyncMeta, fmtLastSync } from "@/lib/templateSyncMeta";
import GolfSyncCard from "@/components/dashboard/GolfSyncCard";
import EspnSyncTab from "@/components/admin/EspnSyncTab";

const FROM_STYLE = {
    api: "text-cyber",
    csv: "text-volt",
    gotsport: "text-cyber",  // iter87.180.12
    manual: "text-zinc-400",
};
const FROM_LABEL = {
    api: "From API",
    csv: "From CSV",
    gotsport: "From GotSport",  // iter87.180.12
    manual: "Manual",
};

// Detect the surface flavour for the header card.
//
// iter87.143 — GotSport / CSV / API-Football entitlements live on the
// LEAGUE, not the tournament. Previously this only looked at
// tournament-scoped flags, so custom tournaments in a Rivalry with
// GotSport enabled fell through to `manual` mode and the operator
// never saw the GotSport sync + schedule cards. Now we also detect
// mode via (a) the tournament's chosen data_source from the custom
// wizard, and (b) the league-level entitlement flags for custom
// tournaments.
function detectMode(tournament, league, meta) {
    if (meta.manual) return "manual";
    if (tournament?.format === "golf") return "golf";
    // iter87.180.12 — GotSport must be its own mode so the header
    // titles, badge copy, and CSV upload card can differentiate it
    // from a genuine CSV feed. Previously collapsed into "csv" and
    // showed misleading "LIVE SYNC — CSV FEED" copy on GotSport-only
    // tournaments.
    const ds = tournament?.data_source;
    const feedCsv = !!tournament?.feed_csv_enabled || ds === "csv";
    const gotsportOnly = (!!tournament?.gotsport_enabled || ds === "gotsport") && !feedCsv;
    if (gotsportOnly) return "gotsport";
    // Explicit tournament-level opt-ins (legacy templates).
    if (tournament?.feed_csv_enabled || tournament?.gotsport_enabled) return "csv";
    // iter87.143 — Custom tournaments: derive mode from the wizard's
    // chosen data_source + league-level entitlements.
    const isCustom = tournament?.template === "custom";
    if (isCustom) {
        if (ds === "gotsport") return "gotsport";  // iter87.180.12
        if (ds === "csv") return "csv";
        if (ds === "api_football") return "api";
        // Legacy custom (pre-iter83.5) with no data_source: fall through
        // based on league entitlements — GotSport-only maps to gotsport,
        // CSV-only maps to csv. Both must be checked (iter87.143):
        //   league?.gotsport_enabled || league?.feed_csv_enabled
        if (!ds) {
            const anyLeagueFeed = league?.gotsport_enabled || league?.feed_csv_enabled;
            if (anyLeagueFeed) {
                if (league?.gotsport_enabled && !league?.feed_csv_enabled) return "gotsport";
                return "csv";
            }
        }
    }
    return "api";
}

function ApiHeader({ meta, tournament }) {
    return (
        <div className="brutal-card p-5 space-y-4" data-testid="live-sync-api-card">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-neon border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <Radio className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                    <h2 className="font-display font-extrabold text-xl uppercase leading-tight" data-testid="live-sync-template-label">
                        Live Sync — {meta.api}
                    </h2>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                        {tournament?.name || meta.template_label}
                    </p>
                </div>
            </div>

            <div className="brutal-card-light p-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-zinc-400 leading-relaxed flex-1 min-w-[12rem]">
                    Scores {meta.refresh_cadence.toLowerCase().startsWith("every") ? "auto-refresh " : ""}
                    <b className="text-zinc-200">{meta.refresh_cadence.replace(/^Every /i, "every ")}</b>
                    {" "}from the cached vendor feed. Sync is managed by the platform — no manual action needed.
                </p>
                <span
                    data-testid="live-sync-auto-chip"
                    className="pill bg-neon/10 border-neon text-neon text-[10px] font-bold px-2.5 py-1 shrink-0"
                >
                    Auto-synced · cached feed
                </span>
            </div>

            <p className="text-[10px] text-zinc-600 leading-relaxed italic" data-testid="live-sync-complete-note">
                Once this tournament is marked Complete, the platform pollers stop calling the vendor for it — every subsequent read is served from the frozen cache.
            </p>

            {meta.docs_url && (
                <a
                    href={meta.docs_url}
                    target="_blank"
                    rel="noreferrer"
                    className="brutal-btn-ghost text-[10px] py-1 px-2 inline-flex items-center gap-1 self-start"
                    data-testid="live-sync-docs-link"
                >
                    <ExternalLink className="w-3 h-3" /> API docs
                </a>
            )}

            {meta.secondary && (
                <div
                    className="brutal-card-light p-3 border-l-4 border-cyber/40"
                    data-testid="live-sync-secondary-card"
                >
                    <p className="text-[9px] uppercase tracking-widest text-cyber/80 font-bold">
                        Secondary source
                    </p>
                    <p className="text-xs text-zinc-300 mt-1 leading-relaxed">
                        <b className="text-zinc-100" data-testid="live-sync-secondary-api">{meta.secondary.api}</b>
                        <span className="text-zinc-500"> · {meta.secondary.scope}</span>
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
                        {meta.secondary.refresh_cadence}
                    </p>
                    {meta.secondary.docs_url && (
                        <a
                            href={meta.secondary.docs_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-[10px] text-cyber hover:underline inline-flex items-center gap-1 mt-1"
                            data-testid="live-sync-secondary-docs-link"
                        >
                            <ExternalLink className="w-3 h-3" /> {meta.secondary.provider}
                        </a>
                    )}
                </div>
            )}
        </div>
    );
}

function ManualHeader({ meta, tournament }) {
    return (
        <div className="brutal-card p-5 space-y-3" data-testid="live-sync-manual-card">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-zinc-500 border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <FileText className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                    <h2 className="font-display font-extrabold text-xl uppercase leading-tight" data-testid="live-sync-template-label">
                        Live Sync — Manual
                    </h2>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                        {tournament?.name || meta.template_label}
                    </p>
                </div>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                There is no live sync for this tournament — you enter every score and mark every round complete by hand from the Schedule + Bracket Status tiles.
            </p>
        </div>
    );
}

function CsvHeader({ meta, tournament, lastSyncIso }) {
    return (
        <div className="brutal-card p-5 space-y-3" data-testid="live-sync-csv-card">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-volt border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <Radio className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                    <h2 className="font-display font-extrabold text-xl uppercase leading-tight" data-testid="live-sync-template-label">
                        Live Sync — CSV feed
                    </h2>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                        {tournament?.name || meta.template_label}
                    </p>
                </div>
            </div>
            <div className="brutal-card-light p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-[12rem] flex-1">
                    <p className="text-[9px] uppercase tracking-widest text-zinc-500">Last CSV upload</p>
                    <p className="font-mono font-bold text-sm mt-0.5" data-testid="live-sync-csv-last">
                        {fmtLastSync(lastSyncIso)}
                    </p>
                </div>
                <span className="pill bg-volt/15 border-volt text-volt text-[10px] font-bold shrink-0">
                    Admin-managed feed
                </span>
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
                CSV feeds are refreshed each time you upload — the panel below hosts the upload + parse action.
            </p>
        </div>
    );
}

// iter87.180.12 — Dedicated GotSport header. Same visual family as
// the CSV card but title, timestamp label, badge, and blurb all
// reference GotSport so admins don't wonder why "CSV Upload" is
// showing on a GotSport-only tournament.
function GotSportHeader({ meta, tournament, lastSyncIso }) {
    return (
        <div className="brutal-card p-5 space-y-3" data-testid="live-sync-gotsport-card">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <Radio className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <div className="min-w-0">
                    <h2 className="font-display font-extrabold text-xl uppercase leading-tight" data-testid="live-sync-template-label">
                        Live Sync — GotSport feed
                    </h2>
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 truncate">
                        {tournament?.name || meta.template_label}
                    </p>
                </div>
            </div>
            <div className="brutal-card-light p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="min-w-[12rem] flex-1">
                    <p className="text-[9px] uppercase tracking-widest text-zinc-500">Last GotSport sync</p>
                    <p className="font-mono font-bold text-sm mt-0.5" data-testid="live-sync-gotsport-last">
                        {fmtLastSync(lastSyncIso)}
                    </p>
                </div>
                <span className="pill bg-cyber/15 border-cyber text-cyber text-[10px] font-bold shrink-0">
                    Auto-synced · GotSport
                </span>
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed">
                Fixture list, final scores, and team rosters pull directly from the linked GotSport division. The panel below lets you refresh on demand or change which division is bound.
            </p>
        </div>
    );
}

export default function LiveSyncTab({ league, leagueId, tournament, isLocked, onChanged }) {
    const meta = getTemplateSyncMeta(tournament);
    // iter87.143 — pass `league` in so custom tournaments detect their
    // real feed mode from the league-level entitlements.
    const mode = detectMode(tournament, league, meta);
    const lastSyncIso = tournament?.last_sync_at
        || tournament?.espn?.last_synced_at
        || null;

    const isCsv = mode === "csv";
    const isGotSport = mode === "gotsport";  // iter87.180.12
    const isGolf = mode === "golf";
    const isManual = mode === "manual";
    // iter87.143 — Needed for EspnSyncTab, which internally gates the
    // GotSport / CSV / API-Football cards on `isCustom && league.<flag>`.
    const isCustom = tournament?.template === "custom";

    return (
        <div className="space-y-5" data-testid="live-sync-tab">
            {isGolf && <GolfSyncCard tournament={tournament} onSynced={onChanged} />}
            {mode === "api" && <ApiHeader meta={meta} tournament={tournament} />}
            {isCsv && <CsvHeader meta={meta} tournament={tournament} lastSyncIso={lastSyncIso} />}
            {isGotSport && <GotSportHeader meta={meta} tournament={tournament} lastSyncIso={tournament?.gotsport_last_synced_at || lastSyncIso} />}
            {isManual && <ManualHeader meta={meta} tournament={tournament} />}

            {/* Actionable feed operations — CSV / GotSport only.
                iter87.143 — pass `isCustom` so EspnSyncTab renders the
                GotSport / CSV / API-Football cards on custom tournaments. */}
            {(isCsv || isGotSport) && (
                <EspnSyncTab
                    league={league}
                    leagueId={leagueId}
                    tournament={tournament}
                    isCustom={isCustom}
                    isLocked={isLocked}
                    onChanged={onChanged}
                />
            )}

            <div className="brutal-card p-5 space-y-3" data-testid="live-sync-field-map">
                <div className="flex items-center justify-between">
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-tight">Read-only field map</h3>
                    <span className="text-[10px] text-zinc-500">What comes from where</span>
                </div>
                <ul className="space-y-1.5">
                    {meta.fields.map((f) => (
                        <li key={f.field} className="flex items-center justify-between gap-3 py-1.5 border-b border-white/[0.04] last:border-b-0">
                            <span className="text-xs text-zinc-300 min-w-0 flex-1">
                                {f.field}
                                {f.note && <span className="block text-[10px] text-zinc-500 italic mt-0.5">{f.note}</span>}
                            </span>
                            <span className={`text-[10px] uppercase font-bold tracking-wider whitespace-nowrap ${FROM_STYLE[f.from] || FROM_STYLE.manual}`}>
                                {FROM_LABEL[f.from] || FROM_LABEL.manual}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}
